import type React from 'react';
import type { ApolloClient } from '@apollo/client';
import type { AnySchema } from 'yup';
import type { Observable } from 'rxjs';
import type { GraphType, Permission, Scalars, SwarmType } from '@/graphql/types';
import type { AxisType } from '../../../../graph/axis';
import type { Graph } from '../../../../graph/graph';
import type { DataTypeValue, ScaleType } from '../../../../graph/enums';
import type { DataSet } from '../../../../graph/dataset';
import type { Scales } from '../../../../graph/graph-helpers';
import type { LegendData } from '../../../../graph/legend-helpers';
import type { ValueScaleEquationType } from '../../../../graph/auto-scale';
import type { Profile } from '../../../../profile';
import type { StructurePath } from './structurePath';
import type { PageSettings } from '../pageSettings';
import type { ReportStructureController } from '../reportStructureController';
import type { RuntimeDataServiceId } from '../runtime/dataServices/contracts';
import type {
    ActiveBindingObserverConfig,
    ActiveDataBinding,
    BindingId,
    RuntimeBindingResolveContext,
    RenderRuntimeDataServicesDebugApi,
} from '../runtime/dataServices/types';
import type { SupportedSwarmTypes } from '../swarmCompatibility';

export type RenderContextKind = 'project' | 'measuring-point';
export type RenderSurface = 'preview' | 'editor' | 'export';
type GraphqlIdInput = Scalars['ID']['input'];

export interface MeasuringPointModel {
    id: GraphqlIdInput;
    name: string;
    deviceName: string | null;
    swarmType: SwarmType;
    permission: Permission;
}

export interface ProjectModel {
    id: GraphqlIdInput;
    name: string;
    timezone: string;
}

export interface ProjectRenderContext {
    kind: 'project';
    project: ProjectModel;
}

export interface MeasuringPointRenderContext {
    kind: 'measuring-point';
    measuringPoint: MeasuringPointModel;
}

export type RenderContext = ProjectRenderContext | MeasuringPointRenderContext;

interface RenderContextByKind {
    project: ProjectRenderContext;
    'measuring-point': MeasuringPointRenderContext;
}

export type ContextForKind<TKind extends RenderContextKind> = RenderContextByKind[TKind];

export interface ElementDefinition {
    type: string;
    settings: Record<string, unknown>;
}

export type ElementLayoutFlow = 'fixed' | 'flow' | 'derived';

export type ElementLayoutMetadata =
    | {
          readonly flow: 'fixed';
      }
    | {
          readonly flow: 'flow';
      }
    | {
          readonly flow: 'derived';
          readonly getChildDefinitionLists: (
              settings: ElementSettings
          ) => readonly (readonly ElementDefinition[])[];
      };

interface RawRuntimeBinding {
    bindingId: BindingId;
    arg?: string;
}

export type ElementDefinitionWithSettings<
    TType extends string,
    TSettings extends Record<string, unknown>
> = ElementDefinition & {
    type: TType;
    settings: TSettings;
};

export type DefinitionSettingsWithRawBinding<TSettings extends { binding: unknown }> = Omit<
    TSettings,
    'binding'
> & {
    binding: RawRuntimeBinding | null;
};

export type BoundElementDefinition<
    TType extends string,
    TSettings extends Record<string, unknown> & { binding: unknown }
> = ElementDefinitionWithSettings<TType, DefinitionSettingsWithRawBinding<TSettings>>;

export function createBoundElementDefinition<
    TType extends string,
    TSettings extends Record<string, unknown> & { binding: unknown }
>(
    type: TType,
    settings: DefinitionSettingsWithRawBinding<TSettings>
): BoundElementDefinition<TType, TSettings> {
    return {
        type,
        settings,
    };
}

export interface BuildStructureSettingsYupSchemaHelpers {
    buildDefinitionsSchema: () => AnySchema;
    buildRequiredDefinitionsSchema: () => AnySchema;
}

export type ElementInsertionSlotKind = 'template-root' | 'child-list';

export interface ElementInsertionPresetDefinition<
    TDefinition extends ElementDefinition = ElementDefinition
> {
    readonly key: string;
    readonly label: string;
    readonly description?: string;
    readonly sourceLabel?: string;
    readonly compatibleContextKinds: readonly RenderContextKind[];
    readonly supportedSwarmTypes: SupportedSwarmTypes;
    readonly createDefinition: () => TDefinition;
}

export interface ElementInsertionPresetOption<
    TDefinition extends ElementDefinition = ElementDefinition
> {
    readonly key: string;
    readonly label: string;
    readonly description?: string;
    readonly sourceLabel?: string;
    readonly supportedSwarmTypes: SupportedSwarmTypes;
    readonly isEnabled: boolean;
    readonly createDefinition: () => TDefinition;
}

interface ResolveElementInsertionPresetsArgs {
    context: RenderContext;
    client: ApolloClient<object>;
}

export type ElementInsertionPresetResolver = (
    args: ResolveElementInsertionPresetsArgs
) => Promise<readonly ElementInsertionPresetOption[]>;

export interface ElementInsertionPresetProvider {
    supports(args: { elementType: string; contextKind: RenderContextKind }): boolean;
    resolvePresets(
        args: ResolveElementInsertionPresetsArgs & { elementType: string }
    ): Promise<readonly ElementInsertionPresetOption[]>;
}

export interface ElementInsertionOption<TDefinition extends ElementDefinition = ElementDefinition> {
    readonly type: string;
    readonly label: string;
    readonly isEnabled: boolean;
    readonly createDefinition: () => TDefinition;
    readonly hasPresetSelector: boolean;
    readonly resolvePresets: ElementInsertionPresetResolver;
}

export interface ElementRenderResult {
    node: React.ReactNode;
    ready: Promise<void>;
}

export interface ElementContentRenderResult extends ElementRenderResult {
    // Renderable content can depend on asynchronous descendants such as repeats and
    // included templates. Every element must report it explicitly so containers
    // never silently assume that an unresolved child produced content.
    hasRenderableContent: Promise<boolean>;
}

export interface ElementRenderState {
    isSwarmCompatible: boolean;
}

export interface PageProfile extends PageSettings {
    pageName: string;
    pageClassName: string;
    sizeCss: string;
    width: string;
    height: string;
}

export interface RenderRuntimeGraphSettings {
    viewStartTs: number;
    viewEndTs: number;
    preferredGraphType: GraphType;
    frequencyGraphScaleType: ScaleType;
    dataType: DataTypeValue;
    valueScale: number | null;
    valueScaleEquation: ValueScaleEquationType;
    frequencyScale: number | null;
    hiddenLines: string[];
    getAlarmsUrl(measuringPointId: GraphqlIdInput): string;
}

export type ElementGraphRuntimeSettings = Partial<
    Pick<
        RenderRuntimeGraphSettings,
        | 'preferredGraphType'
        | 'frequencyGraphScaleType'
        | 'dataType'
        | 'valueScale'
        | 'valueScaleEquation'
        | 'frequencyScale'
        | 'hiddenLines'
    >
> & {
    // Kept distinct from `hiddenLines` so the report/element merge can resolve axis
    // visibility and unrelated legend-line visibility independently. See
    // mergeElementGraphRuntimeSettings in graph/runtimeSettings.ts.
    enabledAxes?: AxisType[];
};

export interface GraphTimeWindow {
    viewStartTs: number;
    viewEndTs: number;
}

export interface GraphRuntimeState {
    dataSet: DataSet;
    dataSetRequestError$: Observable<unknown>;
    scales: Scales;
    legendData: LegendData;
    preferredGraphType: GraphType;
    frequencyGraphScaleType: ScaleType;
}

export interface RenderRuntimeDataServices {
    observeBinding<TServiceId extends RuntimeDataServiceId>(
        binding: ActiveDataBinding<TServiceId>,
        observer: ActiveBindingObserverConfig
    ): Observable<unknown>;
    observeBinding<TServiceId extends RuntimeDataServiceId>(
        binding: ActiveDataBinding<TServiceId>,
        observer: ActiveBindingObserverConfig,
        resolveContext?: RuntimeBindingResolveContext
    ): Observable<unknown>;
    debug?: RenderRuntimeDataServicesDebugApi;
}

export interface ElementLocation {
    elementPath: StructurePath;
    listPath: StructurePath;
    contextKind: RenderContextKind;
    slotKind: ElementInsertionSlotKind;
    index: number;
    siblingCount: number;
}

export interface ElementSettingsEditRequest {
    location: ElementLocation;
    context: RenderContext;
}

export interface StructurePathActionHelper {
    canMoveUp: boolean;
    canMoveDown: boolean;
    moveUp(): void;
    moveDown(): void;
    remove(): void;
}

export interface CoreRenderRuntime {
    renderSurface: RenderSurface;
    // The user profile is needed by render paths that format values whose unit depends on
    // it, for example distance-converted vibration values. Exposing it here keeps the
    // conversion in the presentation layer, so a unit change is a re-render rather than a
    // refetch.
    profile: Profile;
    getOrCreatePageProfile(profile: PageSettings): PageProfile;
    getPageProfiles(): readonly PageProfile[];
    withElementLocation<T>(location: ElementLocation, render: () => T): T;
    getCurrentElementLocation(): ElementLocation | null;
    getCurrentElementChildListPath(settingKey: string): StructurePath;
    getCurrentElementChildListItemPath(settingKey: string, index: number): StructurePath;
    getPreviewRuntime(): PreviewRenderRuntime;
    getTemplateIncludeRuntime(templateId: string): PreviewRenderRuntime;
    getElementTypesForContextKind(contextKind: RenderContextKind): readonly string[];
    getElementInsertionOptionsForSlot(
        contextKind: RenderContextKind,
        slotKind: ElementInsertionSlotKind
    ): ElementInsertionOption[];
    observeBinding<TServiceId extends RuntimeDataServiceId>(
        binding: ActiveDataBinding<TServiceId>,
        resolveContext?: RuntimeBindingResolveContext
    ): Observable<unknown>;
    getGraphRuntimeState(
        measuringPoint: MeasuringPointModel,
        elementGraphSettings?: ElementGraphRuntimeSettings
    ): GraphRuntimeState;
    registerRenderedGraph(
        measuringPoint: MeasuringPointModel,
        graph: Graph,
        elementGraphSettings?: ElementGraphRuntimeSettings
    ): () => void;
    finalizeGraphAutoScaling(): void;
    getRenderedGraphs(): Graph[];
    createElementFromDefinition(definition: ElementDefinition): ReportElement;
    containsFlowableElement(definitions: readonly ElementDefinition[]): boolean;
}

interface EditorRuntimeCapabilities {
    structureController: ReportStructureController;
}

export interface EditorRenderRuntime extends CoreRenderRuntime {
    showEditor: true;
    editor: EditorRuntimeCapabilities;
}

export interface PreviewRenderRuntime extends CoreRenderRuntime {
    showEditor: false;
}

export type RenderRuntime = EditorRenderRuntime | PreviewRenderRuntime;

export interface ReportElement {
    readonly type: string;
    readonly compatibleContextKinds: readonly RenderContextKind[];
    isCompatibleWith(ctx: RenderContext): boolean;
    isSwarmCompatibleWith(ctx: RenderContext): boolean;
    toReactNode(ctx: RenderContext, runtime: RenderRuntime): ElementContentRenderResult;
}

export type ElementSettings = Record<string, unknown>;

type ElementSettingsFormFieldType =
    | 'char'
    | 'markdown'
    | 'float'
    | 'choice'
    | 'boolean'
    | 'stringList'
    | 'swarmTypes'
    | 'definitions'
    | 'group'
    | 'rows';

export interface ElementSettingsFormField {
    name: string;
    label: string;
    helpText?: string;
    readOnly?: boolean;
    type: ElementSettingsFormFieldType;
    fields?: readonly ElementSettingsFormField[];
    columns?: 1 | 2 | 4;
    itemLabel?: string;
    minRows?: number;
    maxRows?: number;
    required?: boolean;
    minValue?: number;
    minItems?: number;
    minLength?: number;
    nonEmptyWhenDefined?: boolean;
    options?: Array<[string, string]>;
    validateOptions?: boolean;
    saveAsNullWhenMissingFields?: readonly string[];
    // Extends the validator built from the metadata above with rules it cannot express, such as a
    // bound that depends on a sibling field. Must be a stable reference: the settings form
    // memoizes the schema by deep comparison.
    buildValidationSchema?: (baseSchema: AnySchema) => AnySchema;
    default: unknown;
}

export interface ElementSettingsFormSchema {
    fields: readonly ElementSettingsFormField[];
}

export interface ResolveElementSettingsFormSchemaArgs {
    contextKind: RenderContext['kind'];
    context: RenderContext;
    client: ApolloClient<object>;
    runtimeDataServices: RenderRuntimeDataServices;
    currentSettings: ElementSettings;
}

// Constructor contract for the registry (class/static side), separate from
// ReportElement instance members like toReactNode and compatibleContextKinds.
export interface ReportElementConstructor<
    TType extends string = string,
    TKind extends RenderContextKind = RenderContextKind,
    TDefinition extends ElementDefinition = ElementDefinition
> {
    readonly elementType: TType;
    readonly layout: ElementLayoutMetadata;
    readonly compatibleContextKinds: readonly TKind[];
    readonly insertion: {
        readonly label: string;
        readonly hiddenFromMenu?: boolean;
        readonly supportedSlots: readonly ElementInsertionSlotKind[];
        readonly createDefinition: () => TDefinition;
        readonly presets?: readonly ElementInsertionPresetDefinition<TDefinition>[];
    };
    readonly settingsFormSchema?: ElementSettingsFormSchema;
    readonly settingsFormSchemaDependencies?: readonly string[];
    readonly resolveSettingsFormSchema: (
        args: ResolveElementSettingsFormSchemaArgs
    ) => Promise<ElementSettingsFormSchema>;
    readonly validateSettings: (rawSettings: ElementSettings) => void;
    readonly normalizeSettingsForSave?: (
        currentSettings: ElementSettings,
        nextSettings: ElementSettings,
        touchedSettingNames?: ReadonlySet<string>
    ) => ElementSettings;
    readonly buildStructureSettingsYupSchema: (
        helpers: BuildStructureSettingsYupSchemaHelpers
    ) => AnySchema;
    // Constructors are heterogeneous across element classes (`TextSettings`,
    // `PageSettings`, ...). We model the registry boundary as `never` and perform
    // one explicit cast at runtime creation call sites.
    new (settings: never): ReportElement;
}
